import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsBelowLg } from "@/hooks/use-mobile";
import type { WorkspaceSummary } from "../../../types/workspace";
import { WorkspaceCard } from "./WorkspaceCard";
import type { WorkspacePreviewEntry } from "./useWorkspaceCanvasPreviews";

const SCROLL_EDGE_EPSILON = 2;

type WorkspaceHubWorkspacesCarouselProps = {
  workspaces: WorkspaceSummary[];
  previews: Record<string, WorkspacePreviewEntry>;
  cardMutatingId: string | null;
  onOpen: (workspace: WorkspaceSummary) => void;
  onRename: (workspace: WorkspaceSummary, name: string) => void | Promise<void>;
  onDelete: (workspace: WorkspaceSummary) => void | Promise<void>;
};

export function WorkspaceHubWorkspacesCarousel({
  workspaces,
  previews,
  cardMutatingId,
  onOpen,
  onRename,
  onDelete,
}: WorkspaceHubWorkspacesCarouselProps): React.ReactElement {
  const isBelowLg = useIsBelowLg();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !isBelowLg) {
      setCanScrollBack(false);
      setCanScrollForward(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollBack(scrollLeft > SCROLL_EDGE_EPSILON);
    setCanScrollForward(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_EPSILON);
  }, [isBelowLg]);

  useEffect(() => {
    updateScrollButtons();
  }, [isBelowLg, workspaces.length, updateScrollButtons]);

  useEffect(() => {
    if (!isBelowLg) return;

    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => updateScrollButtons();
    el.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(onScroll) : null;
    resizeObserver?.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
    };
  }, [isBelowLg, workspaces.length, updateScrollButtons]);

  const scrollByCard = useCallback((direction: "back" | "forward") => {
    const el = scrollRef.current;
    if (!el) return;

    const slide = el.querySelector<HTMLElement>("[data-workspace-hub-card-slide]");
    const gap = Number.parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || "16");
    const step = slide ? slide.offsetWidth + gap : el.clientWidth;

    el.scrollBy({
      left: direction === "forward" ? step : -step,
      behavior: "smooth",
    });
  }, []);

  const showNav = isBelowLg && workspaces.length > 1;

  return (
    <div data-workspace-hub-cards-carousel="" className="flex min-w-0 flex-col gap-3">
      <div
        ref={scrollRef}
        data-workspace-hub-cards-scroll=""
        className={cn(
          "min-w-0 gap-4 lg:grid lg:grid-cols-2 lg:overflow-visible xl:grid-cols-3",
          isBelowLg
            ? "flex snap-x snap-mandatory overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "grid"
        )}
      >
        {workspaces.map((workspace) => {
          const preview = previews[workspace.id];
          return (
            <div
              key={workspace.id}
              data-workspace-hub-card-slide=""
              className={cn(
                isBelowLg ? "w-full min-w-full shrink-0 snap-start" : "min-w-0"
              )}
            >
              <WorkspaceCard
                workspace={workspace}
                onOpen={onOpen}
                onRename={onRename}
                onDelete={onDelete}
                mutating={cardMutatingId === workspace.id}
                previewGraph={preview?.status === "ready" ? preview.graph : null}
                previewLoading={!preview || preview.status === "loading"}
              />
            </div>
          );
        })}
      </div>

      {showNav ? (
        <div className="flex gap-2 lg:hidden">
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="flex-1"
            disabled={!canScrollBack}
            aria-label="Previous workspace"
            onClick={() => scrollByCard("back")}
          >
            <ChevronLeft data-icon="inline-start" className="size-4" aria-hidden />
            <span className="normal-case tracking-normal">Previous</span>
          </Button>
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="flex-1"
            disabled={!canScrollForward}
            aria-label="Next workspace"
            onClick={() => scrollByCard("forward")}
          >
            <span className="normal-case tracking-normal">Next</span>
            <ChevronRight data-icon="inline-end" className="size-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
